import axios from "axios";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
// @mui
import { alpha, Box, Button, Card, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
// components
import Page from "src/components/Page";
import PageHeader from "src/components/PageHeader";
// hooks
import useAppContext from "src/hooks/useAppContext";
//
import { WEB_API } from "src/config";
import { toCurrency } from "src/utils/number";
import { ORDER_STATUS, ORDER_STATUS_MAP, ORDER_TYPE } from "src/mocks/constants";
import { Chat, ContentCopy, DeliveryDining, LocationOn, WhatsApp } from "@mui/icons-material";

export default function MyOrders() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const navigate = useNavigate();
  const orderStatuses = useRef({});
  const { enqueueSnackbar } = useSnackbar();

  const { user, deliveryStatus, getIdToken } = useAppContext();

  const getData = async () => {
    if (!user?.userId && !user?.email) {
      // setLoading(false);
      return
    }

    let params = {};
    if (user?.email) {
      const token = await getIdToken();
      params = {
        url: `${WEB_API}/list_orders`,
        method: 'POST',
        data: {
          email: user.email,
          token
        }
      }
    } else {
      params = {
        url: `${WEB_API}/list_orders/${user.userId}`,
        method: 'GET'
      }
    }

    setLoading(true);

    axios.request({
      ...params,
      headers: {
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*'
      }
    }).then(resp => {
      setData(resp.data.sort((a, b) => a.id - b.id).map((e, idx) => ({ ...e, label: `Pedido #${idx + 1}` })));
      const newOrderStatuses = (resp.data || []).reduce((acc, e) => ({ ...acc, [e.id]: e.status }), {});
      for (const orderId of Object.keys(orderStatuses.current)) {
        if (newOrderStatuses[orderId] !== orderStatuses.current[orderId]) {
          showNotification();
          break;
        }
      }
      orderStatuses.current = newOrderStatuses;
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    })
  }

  async function requestPermission() {
    const permission = await Notification.requestPermission();
    return permission;
  }

  async function showNotification() {
    const permission = await requestPermission();
    if (permission !== 'granted') {
      return;
    }
    new Notification('O status do seu pedido mudou!', {
      body: 'Confira!',
      icon: '/logo.jpg'
    });
  }

  useEffect(() => {
    getData();
    const interval = setInterval(getData, 30000);
    requestPermission();
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [user]);

  return (
    <Page title='Meus Pedidos'>
      <PageHeader
        title="Meus Pedidos"
      />

      {!data.length && !loading &&
        <Typography color={'gray'}>
          Nenhum pedido cadastrado
        </Typography>
      }

      {!loading ?
        data.map(e => (
          <Card key={e.id} sx={{ p: 2, mb: 1, bgcolor: alpha(theme.palette.background.default, 0.1) }}>
            <Typography variant="h5">
              {e.label}
            </Typography>

            <Typography variant="body1" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Status: &nbsp;
              <span style={{ color: ORDER_STATUS[e.status]?.color, display: 'inline-flex', alignItems: 'center' }}>
                {e.status === ORDER_STATUS_MAP.DELIVERING && e.type !== ORDER_TYPE.DELIVERY ?
                  <>
                    <LocationOn />
                    <b>Pedido pronto (aguardando retirada)</b>
                  </>
                  :
                  <>
                    {ORDER_STATUS[e.status]?.icon}&nbsp;
                    <b>{e.status}</b>&nbsp;
                  </>
                }
                {e.lastStatusUpdate && `(${format(new Date(Number(e.lastStatusUpdate)), "HH'h'mm")})`}
              </span>
            </Typography>

            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              Forma de entrega:&nbsp;
              {e.type === ORDER_TYPE.DELIVERY ? <DeliveryDining /> : <LocationOn />}&nbsp;
              <b>{e.type}</b>
            </Typography>

            <Typography variant="h6">
              <b>Itens</b>
            </Typography>

            {e.items?.map((i, idx) => (
              <Box key={`${e.id}-${idx}`} sx={{ mb: 1 }}>
                <Typography>
                  {i.name} - {toCurrency(Number(i.price))} - Quantidade: {i.q}
                </Typography>

                {
                  i.flavors?.length &&
                  <Typography>
                    <b>Sabor(es):</b> {i.flavors.join(', ')}
                  </Typography>
                }

                {
                  i.removed &&
                  <Typography>
                    <b>Ingredientes removidos:</b> {i.removed.join(', ')}
                  </Typography>
                }

                {i.obs &&
                  <Typography>
                    <b>Observações:</b> {i.obs}
                  </Typography>
                }
              </Box>
            ))}

            <Typography variant="h6">
              Total: <b>{toCurrency(Number(e.total))}</b>
            </Typography>

            {user?.district &&
              <>
                <Typography variant="body1">
                  Taxa de Entrega:
                </Typography>

                <Typography variant="h6">
                  + {deliveryStatus?.districts?.find(e => e.name === user.district)?.tax}
                </Typography>
              </>
            }

            <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/chat?id=${e.id}`)}
                sx={{ mt: 1, width: { xs: '100%', sm: 'fit-content' } }}
                startIcon={<Chat />}
              >
                Enviar Mensagem
              </Button>

              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => {
                  navigator.clipboard.writeText('27997791499');
                  enqueueSnackbar('Chave PIX copiada!', { variant: 'success' });
                }}
                sx={{ width: { xs: '100%', sm: 'fit-content' } }}
              >
                Copiar chave PIX
              </Button>

              <Button
                variant="outlined"
                target="_blank"
                href={`https://wa.me/5527997791499?text=${encodeURIComponent(`Olá, sou o(a) ${user?.name} e estou enviando o comprovante de pagamento do meu pedido`)}`}
                startIcon={<WhatsApp />}
                sx={{ textAlign: "center", width: { xs: '100%', sm: 'fit-content' } }}
              >
                Enviar comprovante de pagamento
              </Button>
            </Stack>
          </Card>
        ))
        :
        <CircularProgress size={60} />}
    </Page>
  )
}